// Kanban lead card — renders a single lead's identity, interest, budget,
// broker, and last-updated timestamp. Visually aligned with the design
// comp at `design/src/components/desktop/Leads.jsx` (the inner card body
// at lines 61–75).
//
// Notable visuals:
//   - The interest box uses `bg-card` over the card body's `bg-background`
//     so the two surfaces step.
//   - The property reference reads `"<internalCode> · <buildingName>"`
//     when both are present, matching the design's "BDS-0012 · Tây Hồ
//     Residence" pattern.
//
// What's intentionally NOT here vs an earlier iteration:
//   - The "AI score" sparkles chip + hot-lead top-bar gradient. The user
//     asked for them to be removed; the kanban no longer fakes a score.

import { useState } from "react";
import { MoreHorizontal } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { cn } from "@/lib/utils";
import { formatRelativeVi } from "@/features/property/utils/relative-time";
import { useBrokers } from "@/features/appointment/hooks/use-brokers";
import { LeadStatusSelect } from "@/features/lead/components/lead-status-select";
import { LeadStatusChangeSheet } from "@/features/lead/components/lead-status-change-sheet";
import type { Lead } from "@/features/lead/types";

interface LeadCardProps {
  lead: Lead;
  enableDrag: boolean;
  onOpen: (lead: Lead) => void;
  /**
   * How to surface the status-changer affordance:
   * - `"select"` (default): renders a {@link LeadStatusSelect} dropdown
   *   at the bottom of the card. Used by the drag overlay and any
   *   future tablet+ embedding.
   * - `"pill"`: renders a kebab button in the top-right corner of the
   *   card; tapping it opens a bottom-sheet status picker. Used by
   *   the mobile kanban for a more thumb-friendly experience than a
   *   tap-to-open `<select>` dropdown.
   */
  statusUI?: "select" | "pill";
}

function maskPhoneForMobile(phone: string): string {
  if (phone.length <= 7) return phone;
  return `${phone.slice(0, 4)}***${phone.slice(-3)}`;
}

function formatBudget(min: number | null, max: number | null): string | null {
  const billion = (v: number) => `${(v / 1e9).toFixed(v >= 1e10 ? 0 : 1)} tỷ`;
  if (min !== null && max !== null) return `${billion(min)} — ${billion(max)}`;
  if (min !== null) return `Từ ${billion(min)}`;
  if (max !== null) return `Đến ${billion(max)}`;
  return null;
}

/**
 * Compose the interest line. Prefers `"<code> · <building>"` when both
 * the property's internal code and building name are present (the design's
 * "BDS-0012 · Tây Hồ Residence" format), otherwise falls back to whichever
 * piece is present, and finally to a desired-ward hint or "Đang tìm hiểu".
 */
function propertyInterestLabel(lead: Lead): string {
  if (
    lead.interestedPropertyInternalCode !== null &&
    lead.interestedPropertyBuildingName !== null
  ) {
    return `${lead.interestedPropertyInternalCode} · ${lead.interestedPropertyBuildingName}`;
  }
  return (
    lead.interestedPropertyBuildingName ??
    lead.interestedPropertyInternalCode ??
    (lead.desiredWard ? `Quan tâm ${lead.desiredWard}` : "Đang tìm hiểu")
  );
}

export function LeadCard({
  lead,
  enableDrag,
  onOpen,
  statusUI = "select",
}: LeadCardProps) {
  const sortable = useSortable({
    id: `lead-${lead.id}`,
    data: { type: "lead", lead },
    disabled: !enableDrag,
  });
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = sortable;

  const brokersQuery = useBrokers();
  const broker = brokersQuery.data?.find((b) => b.id === lead.assignedBrokerId);

  // The pill variant manages its own bottom-sheet open state. Defined
  // unconditionally so the hook order is stable regardless of which UI
  // variant is requested.
  const [statusSheetOpen, setStatusSheetOpen] = useState(false);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const interactiveProps = enableDrag
    ? { ...attributes, ...listeners }
    : ({} as Record<string, unknown>);

  const budget = formatBudget(lead.budgetMin, lead.budgetMax);
  const interest = propertyInterestLabel(lead);

  const handleOpen = () => {
    if (isDragging) return;
    onOpen(lead);
  };

  return (
    // Fragment so the LeadStatusChangeSheet is a *sibling* of the card
    // div, not a child. The sheet renders via portal to <body>, but
    // React's synthetic events still bubble through the React tree —
    // so if the sheet were a child of the click-handling div, closing
    // the sheet (X / overlay click / row tap) would bubble up to the
    // card's `onClick` and navigate to /leads/:id. The `data-no-card-
    // open` ancestor check doesn't save us here because `event.target`
    // is the portaled close button, whose DOM ancestors live in
    // <body>, not inside this card.
    <>
    {/* Not a <button> because the card embeds an interactive
        <LeadStatusSelect> when drag is disabled, and button-in-button
        is invalid HTML. We keep the <div> but add role/tabIndex/
        onKeyDown to satisfy jsx-a11y (keyboard parity with the click
        handler). */}
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`lead-card-${lead.id}`}
      role="button"
      tabIndex={0}
      className={cn(
        "group relative flex flex-col gap-2 overflow-hidden rounded-lg border border-border bg-background p-2.5",
        enableDrag ? "cursor-grab active:cursor-grabbing" : "",
        isDragging ? "z-50 opacity-50 shadow-[var(--shadow-card)]" : "",
      )}
      {...interactiveProps}
      onClick={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest("[data-no-card-open]") !== null) return;
        handleOpen();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        const target = event.target as HTMLElement;
        if (target.closest("[data-no-card-open]") !== null) return;
        event.preventDefault();
        handleOpen();
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12px] font-semibold leading-tight">
            {lead.customer.fullName}
          </p>
          <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
            <span className="hidden md:inline">{lead.customer.phoneNumber}</span>
            <span className="md:hidden">
              {maskPhoneForMobile(lead.customer.phoneNumber)}
            </span>
          </p>
        </div>

        {/* Kebab in the top-right when statusUI=="pill" — tap to open
            the bottom-sheet status picker. The card already shows its
            status via the kanban column it's in, so a separate inline
            pill would be redundant; the kebab alone is the action
            affordance. */}
        {statusUI === "pill" ? (
          <button
            data-no-card-open
            type="button"
            aria-label="Đổi trạng thái lead"
            onClick={() => setStatusSheetOpen(true)}
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted"
          >
            <MoreHorizontal aria-hidden="true" className="size-3.5" />
          </button>
        ) : null}
      </div>

      <div className="rounded-md bg-card px-2 py-1.5 text-[11px] leading-snug text-ink-soft">
        {interest}
      </div>

      <div className="flex items-center justify-between gap-2">
        {budget ? (
          <span className="font-mono text-[11px] font-bold tracking-tight text-primary">
            {budget}
          </span>
        ) : (
          <span className="font-mono text-[11px] text-ink-faint">
            Chưa có ngân sách
          </span>
        )}
        <span className="font-mono text-[11px] text-ink-faint">
          {broker?.fullName ?? "—"} · {formatRelativeVi(lead.updatedAt)}
        </span>
      </div>

      {!enableDrag && statusUI === "select" ? (
        <div data-no-card-open className="pt-1">
          <LeadStatusSelect lead={lead} />
        </div>
      ) : null}
    </div>

    {/* Rendered as a sibling of the card div (see Fragment comment
        above) so closing the sheet doesn't bubble back into the
        card's navigate-to-detail handler. */}
    {statusUI === "pill" ? (
      <LeadStatusChangeSheet
        lead={lead}
        open={statusSheetOpen}
        onClose={() => setStatusSheetOpen(false)}
      />
    ) : null}
    </>
  );
}
